/**
 * SSEChatProvider - Custom chat provider for SSE streaming
 * Extends AbstractChatProvider to integrate with @ant-design/x components
 *
 * @module services/chat/sse-provider/sse-provider
 */

import type {
  SSEChatInput,
  SSEChatMessage,
  SSEChatOutput,
  SSETransformMessageInfo,
  SSEXRequestOptions,
} from "./_types";

import { AbstractChatProvider } from "@ant-design/x-sdk";

import { SSERequest } from "./_request";

/**
 * SSE Chat Provider class
 *
 * @remarks
 * This provider manages the chat lifecycle for SSE streaming:
 * - Transforms request parameters for SSE endpoint
 * - Transforms local messages for display
 * - Accumulates streaming chunks into complete messages
 * - Maintains message history
 *
 * @example
 * ```typescript
 * const provider = new SSEChatProvider({
 *   request: new SSERequest('https://api.example.com', {
 *     config: { token: 'Bearer abc123' },
 *   }),
 * });
 *
 * const { onRequest, messages } = useXChat({
 *   provider,
 *   conversationKey: 'session-123',
 * });
 * ```
 */
export class SSEChatProvider<
  ChatMessage extends SSEChatMessage = SSEChatMessage,
  Input extends SSEChatInput = SSEChatInput,
  Output extends SSEChatOutput = SSEChatOutput,
> extends AbstractChatProvider<ChatMessage, Input, Output> {
  /**
   * Transforms request parameters before sending to SSE endpoint
   *
   * @param requestParams - Partial input parameters from user action
   * @param options - Request options with additional params
   * @returns Complete input parameters for SSE request
   *
   * @remarks
   * Handles special cases like 'retry' action by finding the last user message
   * and resending it.
   */
  transformParams(
    requestParams: Partial<Input>,
    options: SSEXRequestOptions<Input, Output>,
  ): Input {
    if (typeof requestParams !== "object") {
      throw new Error("requestParams must be an object");
    }

    // Handle retry action
    if (requestParams.userAction === "retry") {
      const messages = this.getMessages();
      const queryMessage = (messages || []).reverse().find(({ role }) => {
        return role === "user";
      });

      return {
        message: queryMessage as ChatMessage,
        ...(options?.params || {}),
        ...(requestParams || {}),
      } as Input;
    }

    // Normal request
    return {
      ...(options?.params || {}),
      ...(requestParams || {}),
    } as Input;
  }

  /**
   * Transforms local user message for immediate display
   *
   * @param requestParams - Request parameters containing user message
   * @returns Message formatted for display in chat UI
   *
   * @remarks
   * This is called immediately when user sends a message,
   * before the server response arrives.
   */
  transformLocalMessage(requestParams: Partial<Input>): ChatMessage {
    return (requestParams.message || requestParams) as unknown as ChatMessage;
  }

  /**
   * Transforms streaming chunks into accumulated message
   *
   * @param info - Transform info containing original message and new chunk
   * @returns Updated message with accumulated content
   *
   * @remarks
   * This is called for each SSE chunk received. It accumulates the chunks
   * into the complete message content.
   *
   * Handles both simple string content and complex content with text/ext_text.
   */
  transformMessage(info: SSETransformMessageInfo): ChatMessage {
    const { originMessage, chunk } = info || {};

    // If no chunk, return empty assistant message
    if (!chunk) {
      return {
        content:
          typeof originMessage?.content === "object"
            ? originMessage.content
            : {},
        role: "assistant",
      } as ChatMessage;
    }

    // Skip step and follow-up chunks - handled via separate UI state
    if (
      chunk?.metadata?.type === "step" ||
      chunk?.metadata?.type === "follow_up_queries"
    ) {
      return {
        content:
          typeof originMessage?.content === "object"
            ? originMessage.content
            : { text: originMessage?.content || "" },
        role: "assistant",
      } as ChatMessage;
    }

    // Get original content
    const originalContent =
      typeof originMessage?.content === "object"
        ? originMessage.content
        : { text: originMessage?.content || "" };

    // Accumulate chunk content
    const updatedContent = {
      text: (originalContent.text || "") + (chunk.text || ""),
      ext_text: (originalContent.ext_text || "") + (chunk.ext_text || ""),
    };

    return {
      content: updatedContent,
      role: "assistant",
    } as ChatMessage;
  }
}

/**
 * Factory function to create SSE chat provider with request
 *
 * @param baseURL - Base API URL
 * @param config - SSE request configuration
 * @returns New SSEChatProvider instance
 *
 * @example
 * ```typescript
 * const provider = createSSEChatProvider(
 *   'https://api.example.com',
 *   { token: 'Bearer abc123', debug: true }
 * );
 * ```
 */
export function createSSEChatProvider(
  options: SSEXRequestOptions<SSEChatInput, SSEChatOutput>,
): SSEChatProvider {
  return new SSEChatProvider({ request: new SSERequest(options) });
}
